import React, { ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import withStudentAuth from "../hoc/withStudentAuth";
import { useGetStudentQuery } from "../hooks/api/Students/Auth/useGetStudentQuery";
import { message, Modal, Button as AntDButton } from "antd";
import { useSendVerifyOTPMutation } from "../hooks/api/Students/Auth/useSendOTPMutation";
import { useVerifyOTPMutation } from "../hooks/api/Students/Auth/useVerifyOTPMutation";
import Button from "../components/Button";
import { Book, Calendar, Grid2X2, LogOut, Settings, User } from "lucide-react";
import TimezoneSelector from "../pages/Tutors/Onboarding/LevelTwo/TimezoneSelector";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { useUpdateStudentProfileMutation } from "../hooks/api/Students/Auth/useUpdateStudentProfileMutation";

interface MyComponentProps {
  children: ReactNode;
}

const StudentDashboard = ({ children }: MyComponentProps) => {
  const location = useLocation();

  const Logo = require("../assets/images/logo.png");

  const [level, setLevel] = useState(0);
  const [timezone, setTimezone] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [otp, setOTP] = useState("");
  const { data, isLoading } = useGetStudentQuery();
  const { mutateAsync, isLoading: isSendingOTP } = useSendVerifyOTPMutation();
  const { mutateAsync: updateTimezone, isLoading: isUpdatingTimezone } =
    useUpdateStudentProfileMutation({ timezone });
  const { mutateAsync: verifyUser, isLoading: isVerifyingOTP } =
    useVerifyOTPMutation(otp);

  const [visibility, setVisibility] = useState(false);
  const navigate = useNavigate();

  const handleDragStart = (e: React.DragEvent<HTMLImageElement>) => {
    e.preventDefault();
  };

  const activeLink = "text-primary700 bg-primary50 text-medium";
  const inActiveLink = "text-gray700";

  const getLinkClasses = (path: string) =>
    location.pathname === path
      ? "text-[#3F2ABA] font-semibold"
      : "text-gray-700 hover:text-gray-600";

  const handleInitialize = () => {
    mutateAsync();
    setLevel(1);
  };

  const handleVerify = () => {
    verifyUser();
  };

  const handleLogout = () => {
    localStorage.removeItem("W2ST_STUDENT_TOKEN");
    localStorage.removeItem("hasSeenWelcomeLoader");
    message.success("Logout successful");
    navigate("/login");
  };

  const usTimezones = Object.fromEntries(
    Object.entries(allTimezones).filter(
      ([key]) => key.startsWith("US/") || key.includes("America/")
    )
  );

  const handleUpdateTimezone = () => {
    updateTimezone();
  };

  return (
    <>
      <Modal
        width="400px"
        title="Edit Timezone"
        open={visibility}
        footer={null}
        onCancel={() => setVisibility(false)}
      >
        <div className="py-4">
          <label htmlFor="timezone" className="block text-gray-700 mb-2">
            Select your timezone
          </label>
          <div className="select-wrapper">
            <TimezoneSelect
              value={data?.data?.timezone || timezone}
              onChange={(timezone: any) => {
                setTimezone(timezone.value);
              }}
              timezones={usTimezones}
              placeholder="Select timezone"
            />
          </div>
          <div className="mt-6">
            <AntDButton
              type="primary"
              block
              size="large"
              onClick={() => {
                handleUpdateTimezone();
                setVisibility(false);
              }}
              loading={isUpdatingTimezone}
              disabled={timezone === ""}
            >
              Update Timezone
            </AntDButton>
          </div>
        </div>
      </Modal>
      <Modal
        width="400px"
        footer={null}
        closeIcon={false}
        open={data?.data?.isEmailVerified === true && !data?.data?.timezone}
        onCancel={() => {
          setVisibility(false);
        }}
      >
        <h3 className="text-[20px] W2STMedium">Update timezone information</h3>
        <label htmlFor="timezone" className="block mt-4">
          Select your timezone
        </label>
        <div className="select-wrapper mt-3">
          <TimezoneSelect
            value={timezone}
            onChange={(timezone: any) => {
              setTimezone(timezone.value);
            }}
            timezones={usTimezones}
            placeholder="Select Option"
          />

          <AntDButton
            type="primary"
            block
            size="large"
            className="mt-8"
            onClick={handleUpdateTimezone}
            loading={isUpdatingTimezone}
            disabled={timezone === ""}
          >
            Continue
          </AntDButton>
        </div>
      </Modal>
      <Modal
        width="400px"
        footer={null}
        closeIcon={false}
        open={data?.data?.isEmailVerified === false}
        onCancel={() => {
          setVisibility(false);
        }}
      >
        <h3 className="text-[20px] W2STMedium">Email Verification</h3>
        <p className="text-gray-600 mb-6">
          Proceed to verify your email address. An OTP been sent to your email
          for verfication purposes.
        </p>
        {level === 0 ? (
          <div className="block">
            <Button
              label="Proceed"
              isFormValid={true}
              isLoading={isSendingOTP}
              handleSubmit={() => handleInitialize()}
            />
          </div>
        ) : (
          <div>
            <label htmlFor="otp" className="font-gray mt-3 block">
              Email OTP
            </label>
            <input
              name="otp"
              type="text"
              placeholder=""
              value={otp}
              disabled={isVerifyingOTP}
              className="px-6 py-4 my-3 rounded-md w-full cursor-pointer bg-slate-100"
              onChange={(e) => setOTP(e.target.value)}
            />
            <Button
              label="Proceed"
              isFormValid={true}
              isLoading={isVerifyingOTP}
              handleSubmit={() => handleVerify()}
            />
          </div>
        )}
      </Modal>

      {/* Desktop Layout */}
      <div className="md:block hidden overflow-y-hidden max-h-svh">
        <div className="flex flex-col h-svh">
          <div className="border-b-[1px] border-b-gray300 flex-none">
            <div className="container mx-auto flex justify-between items-center p-4 bg-white">
              <span className="flex gap-3 items-center">
                <Link to="/">
                  <img
                    src={Logo}
                    alt="W2ST Logo"
                    className="h-[40px]"
                    onDragStart={handleDragStart}
                  />
                </Link>

                <span className="flex gap-3 ml-6 text-sm">
                  <Link
                    to="/student/overview"
                    className={`p-2 rounded-md flex gap-2 items-center ${
                      location?.pathname.includes("student/overview")
                        ? `${activeLink}`
                        : `${inActiveLink}`
                    }`}
                  >
                    {location?.pathname.includes("/student/overview") && (
                      <Grid2X2 />
                    )}
                    Overview
                  </Link>
                  <Link
                    to="/student/tutors"
                    className={`p-2 rounded-md flex gap-2 items-center ${
                      location?.pathname.includes("student/tutors")
                        ? `${activeLink}`
                        : `${inActiveLink}`
                    }`}
                  >
                    {location?.pathname.includes("/student/tutors") && <Book />}
                    Tutors
                  </Link>
                  <Link
                    to="/student/bookings"
                    className={`p-2 rounded-md flex gap-2 items-center ${
                      location?.pathname.includes("student/bookings")
                        ? `${activeLink}`
                        : `${inActiveLink}`
                    }`}
                  >
                    {location.pathname.includes("/student/bookings") && (
                      <Calendar />
                    )}
                    Bookings
                  </Link>
                </span>
              </span>
              <section className="flex gap-4 items-center text-gray500">
                <button
                  onClick={() => setVisibility(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700">{data?.data?.timezone}</span>
                  <Settings size={16} className="text-gray-500" />
                </button>
                <button
                  className="text-red-700 p-3 W2STMedium"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </section>
            </div>
          </div>

          <div className="bg-gray50 grow overflow-y-scroll scrollbar-none">
            <div className="container mx-auto pt-10">
              <div>{children}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden flex flex-col h-[100svh]">
        <div className="flex-none border-b-[1px] border-b-gray-300">
          <div className="container mx-auto flex justify-between items-center p-4 bg-white">
            <span className="flex gap-3 items-center">
              <img src={Logo} alt="W2ST Logo" className="h-[40px]" />
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setVisibility(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-gray-700">{data?.data?.timezone}</span>
                <Settings size={16} className="text-gray-500" />
              </button>
              <button
                onClick={handleLogout}
                className="text-red-600 hover:text-red-700 transition-colors"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="container mx-auto pb-24">{children}</div>
        </div>

        <div className="flex-none fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom">
          <div className="grid grid-cols-3 gap-1">
            <Link
              to="/student/overview"
              className={`flex flex-col items-center justify-center py-3 ${
                location.pathname.includes("/student/overview")
                  ? "text-primary700"
                  : "text-gray-500"
              }`}
            >
              <Grid2X2 size={24} />
              <span className="text-xs mt-1">Overview</span>
            </Link>

            <Link
              to="/student/tutors"
              className={`flex flex-col items-center justify-center py-3 ${
                location.pathname.includes("/student/tutors")
                  ? "text-primary700"
                  : "text-gray-500"
              }`}
            >
              <Book size={24} />
              <span className="text-xs mt-1">Tutors</span>
            </Link>

            <Link
              to="/student/bookings"
              className={`flex flex-col items-center justify-center py-3 ${
                location.pathname.includes("/student/bookings")
                  ? "text-primary700"
                  : "text-gray-500"
              }`}
            >
              <Calendar size={24} />
              <span className="text-xs mt-1">Bookings</span>
            </Link>

            {/* <Link
              to="/student/profile"
              className={`flex flex-col items-center justify-center py-3 ${
                location.pathname.includes("/student/profile")
                  ? "text-primary700"
                  : "text-gray-500"
              }`}
            >
              <User size={24} />
              <span className="text-xs mt-1">Profile</span>
            </Link> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default withStudentAuth(StudentDashboard);
